<script>
/**
 * @component DashboardAlerts
 * @description Pantalla de seguimientos con stats cards, filtros y grilla de pacientes.
 * Muestra alertas agregadas por paciente con navegación a patient-dashboard.
 */
import Spinner from "@/components/ui/Spinner";
import TrackingNav from "@/components/ui/TrackingNav.vue";
import patientService from "@/services/patient";
import userService from "@/services/user";
import helperService from "@/services/helper";
import episodeService from "@/services/episode";
import pagination from "@/mixins/pagination";
import { EVENTS_TYPES } from "@/services/constants";
import { USER_ROLES } from "@/services/constants";
import { mapGetters } from "vuex";
import { isProtocolsEnabled } from "@/services/config";
import debounce from "debounce";
import { differenceInHours, parseISO } from "date-fns";
import moment from "moment";
import { orderBy } from "lodash";

const DEBOUNCE_TIMEOUT_MS = 500;

export default {
  name: "DashboardAlerts",

  mixins: [pagination],

  components: {
    Spinner,
    TrackingNav,
  },

  data() {
    return {
      EVENTS_TYPES,
      patients: [],
      filteredPatients: [],
      doctors: {},

      isProtocolsEnabled,
      isLoading: false,
      isLoadingDoctors: false,
      filterByActiveTreatment: true,

      searchInput: "",
      searchQuery: {},

      query: {
        status: "",
        measurement: "",
        doctor: "",
      },

      measurementOptions: [
        { text: "Rojas", value: "red" },
        { text: "Amarillas", value: "yellow" },
        { text: "Verdes", value: "green" },
        { text: "Sin Revisar", value: "requiresVerification" },
        { text: "Incompletas", value: "incomplete" },
      ],

      // Overwrite pagination mixin
      pagination: {
        limit: 100,
      },
    };
  },

  created() {
    this.getPatients();

    if (!this.isDoctor) {
      this.getDoctors();
    }
  },

  computed: {
    ...mapGetters(["isDoctor"]),

    statusOptions() {
      const filteredObject = {};

      Object.keys(this.EVENTS_TYPES).forEach((key) => {
        if (key !== "closed") {
          filteredObject[key] = this.EVENTS_TYPES[key];
        }
      });

      return {
        ...filteredObject,
        newPatient: {
          friendlyName: "Paciente nuevo",
          status: "new-patient",
        },
      };
    },

    /** @returns {number} Total de pacientes activos (con tratamientos) */
    statActive() {
      if (!this.patients.length) return 0;
      return this.patients.filter((p) => p.assignedTreatments.length).length;
    },

    /** @returns {number} Suma total de alertas rojas */
    statRed() {
      return this.sumAggregate("red");
    },

    /** @returns {number} Suma total de alertas amarillas */
    statYellow() {
      return this.sumAggregate("yellow");
    },

    /** @returns {number} Pacientes sin alertas rojas ni amarillas */
    statGreen() {
      if (!this.filteredPatients || !this.filteredPatients.length) return 0;
      return this.filteredPatients.filter((u) => {
        const agg = u.patient?.aggregates || {};
        return !agg.red && !agg.yellow;
      }).length;
    },
  },

  watch: {
    filterByActiveTreatment() {
      this.filteredPatients = this.filterByActiveTreatment
        ? this.patients.filter((patient) => patient.assignedTreatments.length)
        : this.patients;
      this.pagination.total = this.filteredPatients.length;
    },
  },

  methods: {
    getPatients() {
      this.isLoading = true;

      const query = {};
      this.query.status && (query.status = this.query.status);
      this.query.measurement && (query.measurement = this.query.measurement);
      this.query.doctor && (query.doctors = this.query.doctor);

      const aggregationQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...query,
      };

      // Query by name or using Mongo $text search index
      if (this.searchInput) {
        Object.assign(aggregationQuery, this.searchQuery);
      }

      patientService
        .getAggregates({
          params: aggregationQuery,
        })
        .then((data) => {
          if (!data) {
            return;
          }

          const docs = data.docs;
          if (!docs) {
            return;
          }

          return docs;
        })
        .then(async (res) => {
          if (res.length) {
            this.patients = await Promise.all(
              res.map(async (patient) => {
                const episodeRes = await episodeService.get(patient._id);
                const episodes = episodeRes.docs;
                const activeEpisode = episodes.find(
                  ({ status }) => status === "active"
                );

                patient.assignedTreatments = activeEpisode
                  ? orderBy(
                      (activeEpisode.treatments || [])
                        .filter((treatment) => !treatment.unnasignedAt)
                        .map((treatment) => ({
                          ...treatment,
                          length: moment(treatment.lastTaskScheduledAt).diff(
                            moment(treatment.firstTaskScheduledAt)
                          ),
                        })),
                      "length",
                      ["desc"]
                    )
                  : [];

                return patient;
              })
            );

            this.filteredPatients = this.filterByActiveTreatment
              ? this.patients.filter(
                  (patient) => patient.assignedTreatments.length
                )
              : this.patients;
            this.pagination.total = this.filteredPatients.length;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    goToPatientDashboard(id) {
      helperService.callIfNoSelectedText(() =>
        this.$router.push({
          name: "patient-dashboard",
          params: { id },
        })
      );
    },

    async getDoctors() {
      this.isLoadingDoctors = true;
      try {
        const users = await userService.get({
          roles: [USER_ROLES.DOCTOR.id],
          select: ["firstName", "lastName"],
        });

        this.doctors = users.docs.reduce((acc, doctor) => {
          acc[doctor._id] = doctor;
          return acc;
        }, {});
      } finally {
        this.isLoadingDoctors = false;
      }
    },

    onSearch: debounce(function () {
      if (!this.searchInput) {
        this.searchQuery = {};
        this.getPatients();
        return;
      }

      const input = this.searchInput.split(" ");
      const useSearchIndex = input && (input.length === 1 || input.length > 3);

      // Search up to 3 words (Double names or double last names)
      const useNameIndex = input && input.length > 1 && input.length <= 3;

      if (useSearchIndex) {
        this.searchQuery = {
          $text: {
            $search: this.searchInput,
          },
        };
        this.getPatients();
        return;
      } else if (useNameIndex) {
        Object.assign(
          this.searchQuery,
          helperService.createNameQuery(this.searchInput)
        );
      }

      this.getPatients();
    }, DEBOUNCE_TIMEOUT_MS),

    // Mixin method
    onPageChange() {
      this.getPatients();
    },

    doesPatientRequiresVerification(user) {
      const { lastEvent } = user.patient;
      const { lastEventCreatedAt } = user.patient;
      if (!lastEvent || !lastEventCreatedAt) {
        return true;
      }

      return differenceInHours(new Date(), parseISO(lastEventCreatedAt)) >= 24;
    },

    getPatientStatus(user) {
      const { patient } = user;

      if (patient.status === "closed") {
        return "Cerrado";
      }

      const lastEvent = user.patient.lastEvent;
      if (!lastEvent) {
        return "Paciente nuevo";
      }

      const lastEventWasClosedStatus = lastEvent.status === "closed";
      const isActive = patient.status === "active";

      // This happens when the patient has an active episode but the last event
      // was a closed event from another episode.
      if (isActive && lastEventWasClosedStatus) {
        return "Paciente nuevo";
      }

      const name = lastEvent && lastEvent.form && lastEvent.form.name;
      return name;
    },

    getPatientDoctorsNames(doctors) {
      return helperService.getPatientDoctorsNames(doctors);
    },

    /**
     * @description Devuelve las iniciales del nombre y apellido
     * @param {Object} user - Objeto usuario
     * @returns {string}
     */
    getInitials(user) {
      const first = (user.firstName || "").charAt(0).toUpperCase();
      const last = (user.lastName || "").charAt(0).toUpperCase();
      return `${first}${last}` || "?";
    },

    /**
     * @description Devuelve el nombre del primer tratamiento asignado
     * @param {Array} treatments - Array de tratamientos
     * @returns {string}
     */
    getTreatmentName(treatments) {
      return treatments[0]?.treatment?.name || "Sin nombre";
    },

    /**
     * @description Suma un campo de aggregates sobre los pacientes filtrados
     * @param {string} key - Clave del aggregate (red, yellow, green, incomplete)
     * @returns {number}
     */
    sumAggregate(key) {
      const list = this.filteredPatients || this.patients;
      if (!list || !list.length) return 0;
      return list.reduce((sum, u) => {
        return sum + (u.patient?.aggregates?.[key] || 0);
      }, 0);
    },
  },
};
</script>

<template lang="pug">
section.dashboard-alerts
  tracking-nav(active="seguimientos")

  //- Page header
  .page-header
    .page-header__left
      h1.page-title Seguimientos
      span.page-count(v-if="!isLoading && pagination.total") {{ pagination.total }} pacientes
    .page-header__right
      router-link.btn-protocols(
        v-if="isProtocolsEnabled"
        :to="{ name: 'treatment' }"
      )
        i.el-icon-setting
        span Configurar protocolos

  //- Stats row
  .stats-row
    .stat-card
      .stat-card__icon.stat-card__icon--navy
        i.el-icon-user
      .stat-card__info
        span.stat-card__number {{ statActive }}
        span.stat-card__label Pacientes activos
    .stat-card
      .stat-card__icon.stat-card__icon--red
        i.el-icon-warning
      .stat-card__info
        span.stat-card__number {{ statRed }}
        span.stat-card__label Alertas rojas
    .stat-card
      .stat-card__icon.stat-card__icon--yellow
        i.el-icon-warning-outline
      .stat-card__info
        span.stat-card__number {{ statYellow }}
        span.stat-card__label Alertas amarillas
    .stat-card
      .stat-card__icon.stat-card__icon--green
        i.el-icon-circle-check
      .stat-card__info
        span.stat-card__number {{ statGreen }}
        span.stat-card__label Sin alertas

  //- Filters bar
  .header-bar
    .header-bar__left
      .search-wrapper
        i.el-icon-search.search-icon
        input.search-input(
          v-model="searchInput"
          @input="onSearch"
          placeholder="Buscar paciente..."
        )

      .select-wrapper
        select.filter-select(
          v-model="query.measurement"
          @change="getPatients"
          :disabled="isLoading"
        )
          option(value="") Tipo de alerta
          option(
            v-for="option in measurementOptions"
            :key="option.value"
            :value="option.value"
          ) {{ option.text }}

      .select-wrapper(v-if="!isDoctor")
        select.filter-select(
          v-model="query.doctor"
          @change="getPatients"
          :disabled="isLoadingDoctors"
        )
          option(value="") Médico
          option(
            v-for="doctor in doctors"
            :key="doctor._id"
            :value="doctor._id"
          ) {{ doctor.lastName }}, {{ doctor.firstName }}

    .header-bar__right
      button.toggle-pill(
        :class="{ 'toggle-pill--active': filterByActiveTreatment }"
        @click="filterByActiveTreatment = !filterByActiveTreatment"
      )
        i.el-icon-check(v-if="filterByActiveTreatment")
        span Solo activos

  //- Loading
  spinner(v-if="isLoading")

  //- Empty state
  .empty-state(v-if="!isLoading && !patients.length")
    .empty-state__icon
      i.el-icon-user
    p.empty-state__text No hay resultados

  //- Patient cards grid
  .monitoring-grid(v-if="!isLoading && filteredPatients && filteredPatients.length")
    .monitoring-card(
      v-for="user in filteredPatients"
      :key="user._id"
      @click="goToPatientDashboard(user._id)"
    )
      .monitoring-card__top
        .monitoring-card__patient
          .monitoring-card__avatar {{ getInitials(user) }}
          .monitoring-card__name-block
            span.monitoring-card__name {{ user.firstName }} {{ user.lastName }}
            span.monitoring-card__activity(v-if="user.patient.lastEventCreatedAt") Última actividad: Hace {{ user.patient.lastEventCreatedAt | formatDistanceStrict }}
            span.monitoring-card__activity(v-else) Sin actividad
        .monitoring-card__badges
          span.alert-badge.alert-badge--red(v-if="user.patient.aggregates.red")
            | {{ user.patient.aggregates.red }}
          span.alert-badge.alert-badge--yellow(v-if="user.patient.aggregates.yellow")
            | {{ user.patient.aggregates.yellow }}
          span.alert-badge.alert-badge--green(v-if="user.patient.aggregates.green")
            | {{ user.patient.aggregates.green }}
          span.alert-badge.alert-badge--blue(v-if="user.patient.aggregates.incomplete")
            | {{ user.patient.aggregates.incomplete }}

      .monitoring-card__bottom
        .monitoring-card__protocols
          template(v-if="user.assignedTreatments.length")
            span.protocol-pill
              i.el-icon-document
              | {{ getTreatmentName(user.assignedTreatments) }}
            span.protocol-pill.protocol-pill--count(v-if="user.assignedTreatments.length > 1")
              | +{{ user.assignedTreatments.length - 1 }}
          span.protocol-pill.protocol-pill--empty(v-else) Sin protocolos
        .monitoring-card__status
          span.status-pill.status-pill--review(v-if="doesPatientRequiresVerification(user)") Sin revisar

  //- Pagination
  pagination(
    :isLoading="isLoading"
    :limit="pagination.limit"
    :total="pagination.total"
    @pagination="setPagination"
  )
</template>

<style lang="scss" scoped>
/* ── Page header ── */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header__left {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: #002d5e;
  margin: 0;
}

.page-count {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
}

.btn-protocols {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  color: #002d5e;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  i {
    font-size: 14px;
  }

  &:hover {
    background: #f0f2f7;
    border-color: #cbd5e1;
  }
}

/* ── Stats row ── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  border: 1.5px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-card__icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 18px;
  }
}

.stat-card__icon--navy {
  background: rgba(0, 45, 94, 0.08);
  i {
    color: #002d5e;
  }
}

.stat-card__icon--red {
  background: #fef2f2;
  i {
    color: #dc2626;
  }
}

.stat-card__icon--yellow {
  background: #fffbeb;
  i {
    color: #d97706;
  }
}

.stat-card__icon--green {
  background: #f0fdf4;
  i {
    color: #16a34a;
  }
}

.stat-card__info {
  display: flex;
  flex-direction: column;
}

.stat-card__number {
  font-size: 22px;
  font-weight: 700;
  color: #002d5e;
  line-height: 1.1;
}

.stat-card__label {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

/* ── Filters bar ── */
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
  flex-wrap: wrap;
}

.header-bar__left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #94a3b8;
  pointer-events: none;
}

.search-input {
  width: 280px;
  padding: 9px 14px 9px 38px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: #002d5e;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #002d5e;
    box-shadow: 0 0 0 3px rgba(0, 45, 94, 0.08);
  }

  &::placeholder {
    color: #cbd5e1;
  }
}

.select-wrapper {
  position: relative;

  &::after {
    content: "";
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid #94a3b8;
    pointer-events: none;
  }
}

.filter-select {
  appearance: none;
  padding: 9px 32px 9px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  font-size: 13px;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: #002d5e;
  outline: none;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 140px;

  &:focus {
    border-color: #002d5e;
    box-shadow: 0 0 0 3px rgba(0, 45, 94, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.toggle-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 20px;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: "Plus Jakarta Sans", sans-serif;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  i {
    font-size: 13px;
  }

  &:hover {
    border-color: #cbd5e1;
  }
}

.toggle-pill--active {
  background: #e6f9f9;
  border-color: #2abfbf;
  color: #002d5e;
}

/* ── Empty state ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 12px;
}

.empty-state__icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border-radius: 50%;

  i {
    font-size: 24px;
    color: #94a3b8;
  }
}

.empty-state__text {
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
}

/* ── Monitoring grid ── */
.monitoring-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.monitoring-card {
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  padding: 18px 20px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &:hover {
    border-color: #2abfbf;
    box-shadow: 0 4px 12px rgba(42, 191, 191, 0.12);
  }
}

/* ── Card top row ── */
.monitoring-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.monitoring-card__patient {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.monitoring-card__avatar {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 50%;
  background: #002d5e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
}

.monitoring-card__name-block {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.monitoring-card__name {
  font-size: 15px;
  font-weight: 700;
  color: #002d5e;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.monitoring-card__activity {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.monitoring-card__badges {
  display: flex;
  gap: 5px;
  flex-shrink: 0;
}

.alert-badge {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.alert-badge--red {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fca5a5;
}

.alert-badge--yellow {
  background: #fffbeb;
  color: #d97706;
  border: 1px solid #fcd34d;
}

.alert-badge--green {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #86efac;
}

.alert-badge--blue {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #93c5fd;
}

/* ── Card bottom row ── */
.monitoring-card__bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.monitoring-card__protocols {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.protocol-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;

  i {
    font-size: 11px;
    color: #94a3b8;
  }
}

.protocol-pill--count {
  max-width: none;
  background: #f1f5f9;
  color: #64748b;
}

.protocol-pill--empty {
  color: #94a3b8;
  border-style: dashed;
}

.status-pill--review {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #ea580c;
  white-space: nowrap;
}

/* ── Responsive ── */
@media (max-width: 1024px) {
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .monitoring-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .stats-row {
    grid-template-columns: 1fr;
  }

  .header-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .header-bar__left {
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }

  .filter-select {
    width: 100%;
  }
}
</style>
